import type { ProdutoPricing } from './pricing.service';

// AIDEV-NOTE: Tipo das variantes de grade retornadas pela API — espelho do server
// (produtos-catalogo.helper.ts). Cada variante tem SEUS precos (tamanhos podem diferir).
export interface GradeVariante {
  cor: string;
  tamanho: string;
  saldo_estoque: number;
  produto_codigo: number;
  codcor: number;
  codtamanho: number;
  preco_venda: number;
  preco_atacado: number;
  preco_promocional: number | null;
  precos_por_tabela: Record<number, number>;
}

export interface PrecoTamanho {
  codtamanho: number;
  tamanho: string;
  min: number;
  max: number;
}

const MAX_FAIXAS = 3;

/**
 * Substitui os campos de preco do produto pelos da variante.
 * AIDEV-NOTE: reusa o PricingService com TODA a precedencia (override V/A, promo, tabela, qtd)
 */
export function varianteToPricing(produto: ProdutoPricing, v: GradeVariante): ProdutoPricing {
  // AIDEV-NOTE: fallback p/ cache offline (NetworkFirst 1 dia) com variantes sem os novos campos de preco
  return {
    ...produto,
    PRECO_VENDA: v.preco_venda ?? produto.PRECO_VENDA,
    PRECO_ATACADO: v.preco_atacado ?? produto.PRECO_ATACADO,
    preco_promocional:
      v.preco_promocional === undefined ? produto.preco_promocional : v.preco_promocional,
    precos_por_tabela: v.precos_por_tabela ?? produto.precos_por_tabela ?? {}
  };
}

/**
 * Consolida o preco (min/max) por tamanho, ordenado por codtamanho (mesma ordem da grade).
 */
export function precosPorTamanho(
  variantes: GradeVariante[],
  calcular: (v: GradeVariante) => number
): PrecoTamanho[] {
  const map = new Map<number, PrecoTamanho>();
  for (const v of variantes) {
    const preco = Math.round(calcular(v) * 100) / 100;
    const atual = map.get(v.codtamanho);
    if (!atual) {
      map.set(v.codtamanho, {
        codtamanho: v.codtamanho,
        tamanho: v.tamanho,
        min: preco,
        max: preco
      });
    } else {
      atual.min = Math.min(atual.min, preco);
      atual.max = Math.max(atual.max, preco);
    }
  }
  return [...map.values()].sort((a, b) => a.codtamanho - b.codtamanho);
}

/**
 * Label de faixas para o card: "P–G R$ 24,90 · G1–G3 R$ 34,90".
 * Retorna null quando todos os tamanhos tem o mesmo preco (card mantem exibicao atual).
 * AIDEV-NOTE: >3 faixas OU preco variando por cor no mesmo tamanho → fallback "min – max"
 */
export function agruparFaixasPreco(
  variantes: GradeVariante[],
  calcular: (v: GradeVariante) => number,
  formatar: (valor: number) => string
): string | null {
  const tamanhos = precosPorTamanho(variantes, calcular);
  if (tamanhos.length === 0) return null;

  const min = Math.min(...tamanhos.map((t) => t.min));
  const max = Math.max(...tamanhos.map((t) => t.max));
  if (min === max) return null;

  const variaPorCor = tamanhos.some((t) => t.min !== t.max);
  if (variaPorCor) return `${formatar(min)} – ${formatar(max)}`;

  const faixas: { de: string; ate: string; preco: number }[] = [];
  for (const t of tamanhos) {
    const ultima = faixas[faixas.length - 1];
    if (ultima && ultima.preco === t.min) {
      ultima.ate = t.tamanho;
    } else {
      faixas.push({ de: t.tamanho, ate: t.tamanho, preco: t.min });
    }
  }

  if (faixas.length > MAX_FAIXAS) return `${formatar(min)} – ${formatar(max)}`;

  return faixas
    .map((f) =>
      f.de === f.ate ? `${f.de} ${formatar(f.preco)}` : `${f.de}–${f.ate} ${formatar(f.preco)}`
    )
    .join(' · ');
}
